import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Language } from '../types'
import { translations } from '../data/translations'

const SUPPORTED_LANGUAGES: Language[] = [
  'en',
  'id',
  'ms',
  'th',
  'vi',
  'fil',
  'km',
  'lo',
  'my',
]
const DEFAULT_LANGUAGE: Language = 'en'

export function useLanguage() {
  const navigate = useNavigate()
  const location = useLocation()

  const [language, setLanguage] = useState<Language>(() => {
    // Initialize language from URL on first render
    if (typeof window !== 'undefined') {
      const pathSegments = window.location.pathname.split('/')
      const langSegment = pathSegments[1] as Language
      if (SUPPORTED_LANGUAGES.includes(langSegment)) {
        return langSegment
      }
      // Fallback to localStorage or default
      const savedLang = localStorage.getItem('preferredLanguage') as Language
      return SUPPORTED_LANGUAGES.includes(savedLang)
        ? savedLang
        : DEFAULT_LANGUAGE
    }
    return DEFAULT_LANGUAGE
  })

  // Sync language with URL changes
  useEffect(() => {
    const pathSegments = location.pathname.split('/')
    const langSegment = pathSegments[1] as Language
    if (SUPPORTED_LANGUAGES.includes(langSegment) && langSegment !== language) {
      setLanguage(langSegment)
    }
  }, [location.pathname, language])

  const changeLanguage = useCallback(
    (newLang: Language) => {
      const pathSegments = location.pathname.split('/')
      // Replace the language segment (index 1)
      if (SUPPORTED_LANGUAGES.includes(pathSegments[1] as Language)) {
        pathSegments[1] = newLang
      } else {
        // If no language segment (shouldn't happen with proper routing), insert it
        pathSegments.splice(1, 0, newLang)
      }
      const newPath = pathSegments.join('/')
      navigate(newPath)
    },
    [location.pathname, navigate],
  )

  const t = useCallback(
    (key: string): string => {
      // Defensive check for translations object
      if (!translations || typeof translations !== 'object') {
        console.warn('Translations object is not available')
        return key
      }

      const translation = translations[key]
      if (!translation) {
        // Only log in development to avoid console spam
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation key not found: ${key}`)
        }
        return key
      }

      // Ensure language is valid
      const currentLang = SUPPORTED_LANGUAGES.includes(language)
        ? language
        : DEFAULT_LANGUAGE

      return translation[currentLang] || translation[DEFAULT_LANGUAGE] || key
    },
    [language],
  )

  return {
    language,
    changeLanguage,
    t,
    supportedLanguages: SUPPORTED_LANGUAGES,
  }
}
